import { apiService, authService, echoService } from "@/services";
import { ApiError } from "@/services/api.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { RoutePath } from "@/routes";
import { handleApiError } from "@/utils";
import {
  AccountResponse,
  ForgotPasswordParams,
  ForgotPasswordResponse,
  LoginParams,
  LoginResponse,
  ResetPasswordParams,
  ResetPasswordResponse,
  UpdatePasswordParams,
  UpdatePasswordResponse,
  UpdateProfileParams,
  VerifyResetTokenParams,
  VerifyResetTokenResponse,
} from "@/types";
import queryClient from "@/query-client";
import { DEFAULT_STALE_TIME } from "@/constants";
import { toast } from "sonner";

export const useGetAccount = (options?: object) =>
  useQuery({
    queryKey: ['account'],
    queryFn: () => apiService.get<AccountResponse, unknown>('/user'),
    staleTime: DEFAULT_STALE_TIME,
    ...options,
  });

export const useLogin = () =>
  useMutation<LoginResponse, unknown, LoginParams>({
    mutationFn: (data) =>
      apiService.post('/login', data),
    onSuccess: ({ auth_token: token, expires_in: expiresIn }, variables) => {
      authService.setToken(token, expiresIn, variables.remember);
      window.location.href = RoutePath.Home;
    },
    onError: (error) => handleApiError(error),
  });

// AUT-125: silently extends a non-"remember me" session's server-side
// expiry on real user activity (see useKeepSessionAlive) -- keeps an
// actively-used session from timing out while a truly idle one still
// expires for real.
export const useRefreshToken = () =>
  useMutation<LoginResponse, unknown, void>({
    mutationFn: () => apiService.post('/refresh'),
    onSuccess: ({ auth_token: token, expires_in: expiresIn }) => {
      authService.setToken(token, expiresIn, false);
    },
  });

export const useSignOut = () =>
  useMutation({
    mutationFn: () => apiService.post('/logout'),
    onSuccess: () => {
      echoService.disconnect();
      authService.clearToken();
      queryClient.setQueryData(['account'], null);
      window.location.href = RoutePath.Login;
    },
    onError: (error) => handleApiError(error)
  });

export const useGetAdminSsoUrl = () =>
  useMutation<{ url: string }, unknown, unknown>({
    mutationFn: () => apiService.post('/admin/sso-link'),
  });

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation<AccountResponse, unknown, UpdateProfileParams>({
    mutationFn: (params) =>
      apiService.patch<AccountResponse, UpdateProfileParams>('/profile', params),
    onSuccess: (data) => {
      queryClient.setQueryData(['account'], data);
    },
    onError: (error) => handleApiError(error),
  });
};

export const useUpdatePassword = () =>
  useMutation<UpdatePasswordResponse, unknown, UpdatePasswordParams>({
    mutationFn: (data) =>
      apiService.put<UpdatePasswordResponse, UpdatePasswordParams>('/profile/password', data),
    onSuccess: ({ auth_token, expires_in }) => {
      authService.setToken(auth_token, expires_in, authService.isSessionRemembered());
      toast.success("Password updated successfully");
    },
  });

// AUT-39: password reset (specs/account/password-reset). The three endpoints
// are public and unauthenticated -- see components/account.md § The three
// new endpoints for the request/response shapes these hooks mirror.

export const useForgotPassword = (onSent: () => void) =>
  useMutation<ForgotPasswordResponse, unknown, ForgotPasswordParams>({
    mutationFn: (data) =>
      apiService.post<ForgotPasswordResponse, ForgotPasswordParams>('/forgot-password', data),
    onSuccess: () => {
      onSent();
    },
    onError: (error) => {
      // A 429 must not toast: SendPasswordResetLink is byte-identical for a
      // known and an unknown address, and toasting the throttle differently
      // than a normal send would leak that this address was tried, breaking
      // that non-enumeration guarantee (constraints.md § C-22).
      if (error instanceof ApiError && error.status === 429) {
        onSent();
        return;
      }
      handleApiError(error);
    },
  });

export const getVerifyResetTokenQueryKey = (token: string | null) =>
  ['reset-password', 'verify', token] as const;

export const useVerifyResetToken = (token: string | null) =>
  useQuery<VerifyResetTokenResponse>({
    queryKey: getVerifyResetTokenQueryKey(token),
    queryFn: () => {
      if (!token) {
        // Unreachable in practice: `enabled` keeps this from firing without
        // a token. Narrows `token` for the call below without a `!`.
        return Promise.reject(new Error('No token to verify'));
      }
      return apiService.post<VerifyResetTokenResponse, VerifyResetTokenParams>(
        '/reset-password/verify',
        { token },
      );
    },
    enabled: !!token,
    // No retry override: src/query-client.ts already declines to retry a
    // 4xx (so the 429 from throttle:10,1 is not hammered) and retries a 5xx
    // or dropped connection at most twice -- the shared predicate is correct
    // here too.
  });

export const useResetPassword = () => {
  const navigate = useNavigate();

  return useMutation<ResetPasswordResponse, unknown, ResetPasswordParams>({
    mutationFn: (data) =>
      apiService.post<ResetPasswordResponse, ResetPasswordParams>('/reset-password', data),
    onSuccess: () => {
      // Order matters, and matches useSignOut's (account.api.ts above):
      // disconnect before clearToken. This navigate is soft -- the app
      // stays mounted -- so without tearing down the Reverb subscription
      // first, the tab keeps a live, authorised subscription belonging to
      // the session this reset just revoked server-side
      // (tech-debt.md § reverb-subscriptions-outlive-revocation).
      echoService.disconnect();
      authService.clearToken();
      // AUT-39: WEB-003, FR-17. `position` rides on this one call only --
      // constraints.md § C-28 -- so this toast gets its own top-centre
      // region while every other toast in the product stays in the
      // default region <Sonner /> renders (App.tsx, untouched).
      toast.success('Password updated', {
        description: 'Sign in with your new password.',
        position: 'top-center',
      });
      navigate(RoutePath.Login);
    },
  });
};